
import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
  Box,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  IconButton,
  Chip,
} from "@mui/material";
import {
  AccessTime, People, CalendarMonth, MenuBook, Info, Close, CheckCircle, Download,
  SkipPrevious, PlayArrow, Pause, SkipNext, KeyboardArrowDown
} from "@mui/icons-material";
import Swal from "sweetalert2";

// Main page logos
import logo1 from "./landingpageimages/L1 (1).png";
import logo2 from "./landingpageimages/L2.png";
import logo3 from "./landingpageimages/L3.png";
import logo4 from "./landingpageimages/L4.png";
import logo5 from "./landingpageimages/L5.png";
import logo6 from "./landingpageimages/L6.png";
import logo7 from "./landingpageimages/L7.png";
import side1 from "./landingpageimages/elephanttank2.jpg"
import side2 from "./landingpageimages/homiphoto.jpeg"

import side5 from "./landingpageimages/pic1.JPG"
import side4 from "./landingpageimages/homiphoto.jpeg"
import side3 from "./landingpageimages/pic5.JPG"

// New Header Logos
import hbsuLogo from "./landingpageimages/hbsulogo.png"; 
import datatechLogo from "./landingpageimages/L7.png";
import g20Logo from "./landingpageimages/g20newlogo.png";
import mahagovLogo from "./landingpageimages/govofindia.jpeg"; 

import Chatbot from "../src/Chatbot";

// AppHeader Component (Modified)
const AppHeader = ({ courses }) => {
  const [newsItems, setNewsItems] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [isTickerPaused, setIsTickerPaused] = useState(false);
  const tickerIntervalRef = useRef(null);

  useEffect(() => {
    let items = [];
    if (courses && courses.length > 0) {
      items = courses.map(course => course.program_name).filter(Boolean);
    }
    setNewsItems(items);
    setCurrentNewsIndex(0); // Reset index when courses change
  }, [courses]);

  const handleNextNews = () => {
    if (newsItems.length === 0) return;
    setCurrentNewsIndex((prevIndex) => (prevIndex + 1) % newsItems.length);
  };

  useEffect(() => {
    if (!isTickerPaused && newsItems.length > 0) {
      tickerIntervalRef.current = setInterval(() => {
        handleNextNews();
      }, 5000);
    } else {
      clearInterval(tickerIntervalRef.current);
    }
    return () => clearInterval(tickerIntervalRef.current);
  }, [isTickerPaused, newsItems, currentNewsIndex]); // currentNewsIndex dependency to restart timer if manually changed

  const handlePrevNews = () => {
    if (newsItems.length === 0) return;
    setCurrentNewsIndex((prevIndex) => (prevIndex - 1 + newsItems.length) % newsItems.length);
    if (!isTickerPaused) { // Restart interval if manually navigated and not paused
      clearInterval(tickerIntervalRef.current);
      tickerIntervalRef.current = setInterval(handleNextNews, 5000);
    }
  };

  const togglePauseNews = () => {
    setIsTickerPaused((prev) => !prev);
  };

  return (
    // Added id="app-header-sticky"
    <Box id="app-header-sticky" sx={{ position: 'sticky', top: 0, zIndex: 1200, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <Container maxWidth="xl" disableGutters>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: { xs: 1, sm: 1.5, md: 2 },
          px: { xs: 1, sm: 2, md: 3 },
          flexWrap: 'wrap', // Allow wrapping on smaller screens
        }}
      >
        {/* Group 1: HBSU & DataTech */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0, sm: 1 }, flexBasis: { xs: '100%', sm: 'auto' }, justifyContent: {xs: 'center', sm: 'flex-start'}, mb: {xs: 1, sm: 0} }}>
          {/* HBSU Logo and Text */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 2, md: 3 } }}>
            <Box
              component="img"
              src={hbsuLogo}
              alt="HBSU Logo"
              sx={{
                objectFit: 'contain',
                width: { xs: '60px', sm: '70px', md: '80px' },
                maxHeight: { xs: '60px', sm: '70px', md: '80px' },
              }}
            />
            <Box sx={{ ml: { sm: 1, md: 1.5 }, display: { xs: 'none', sm: 'block' } }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  color: '#002060',
                  lineHeight: 1.1,
                  fontSize: { sm: '1rem', md: '1.3rem' },
                }}
              >
                Dr. Homi Bhabha
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  color: '#002060',
                  lineHeight: 1.1,
                  fontSize: { sm: '1rem', md: '1.3rem' },
                }}
              >
                State University
              </Typography>
            </Box>
          </Box>

          {/* DataTech Logo */}
          <Box
            component="img"
            src={datatechLogo}
            alt="DataTech Labs Logo"
            sx={{
              objectFit: 'contain',
              width: { xs: '90px', sm: '110px', md: '130px' },
              maxHeight: { xs: '35px', sm: '45px', md: '55px' },
            }}
          />
        </Box>

        {/* Group 2: G20 & Mahagov */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, sm: 0 }, flexBasis: { xs: '100%', sm: 'auto' }, justifyContent: {xs: 'center', sm: 'flex-end'} }}>
          {/* G20 Logo */}
          <Box
            component="img"
            src={g20Logo}
            alt="G20 Logo"
            sx={{
              objectFit: 'contain',
              width: { xs: '70px', sm: '90px', md: '110px' },
              maxHeight: { xs: '40px', sm: '50px', md: '60px' },
              mr: { xs: 1, sm: 2, md: 3 },
            }}
          />

          {/* Maharashtra Government Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={mahagovLogo}
              alt="Maharashtra Government Emblem"
              sx={{
                objectFit: 'contain',
                width: { xs: '35px', sm: '45px', md: '50px' },
                maxHeight: { xs: '45px', sm: '55px', md: '65px' },
                mr: { xs: 0.5, sm: 1 },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Container>
      {/* News Ticker */}
      {newsItems.length > 0 && (
        <Box
          sx={{
            backgroundColor: '#8F004A', // HBSU Purple
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            py: 0.75,
            px: { xs: 1, sm: 2 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFC107', // Yellow for "Latest News"
              color: 'black',
              p: '4px 10px',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              mr: { xs: 1, sm: 2 },
              flexShrink: 0,
            }}
          >
            Latest News:
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative', height: '24px' }}>
            <Typography
              key={currentNewsIndex} // For re-triggering animation
              variant="body2"
              sx={{
                position: 'absolute',
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis', // Fallback if animation is too fast or text too long
                animation: `tickerFadeInOut 5s ease-in-out forwards`,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                lineHeight: '24px', // Ensure vertical centering
              }}
            >
              {newsItems[currentNewsIndex]}
              <Chip
                label="New"
                size="small"
                sx={{
                  backgroundColor: 'red',
                  color: 'white',
                  height: '18px',
                  fontSize: '0.65rem',
                  ml: 1,
                  fontWeight: 'bold',
                  transform: 'translateY(-1px)' // Minor adjustment
                }}
              />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 1, sm: 2 }, flexShrink: 0 }}>
            <IconButton size="small" sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }} onClick={handlePrevNews} aria-label="previous news">
              <SkipPrevious fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }} onClick={togglePauseNews} aria-label={isTickerPaused ? "play news" : "pause news"}>
              {isTickerPaused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}
            </IconButton>
            <IconButton size="small" sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }} onClick={handleNextNews} aria-label="next news">
              <SkipNext fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
      <style>
        {`
          @keyframes tickerFadeInOut {
            0% { opacity: 0; transform: translateY(8px); } /* Start from bottom, invisible */
            15% { opacity: 1; transform: translateY(0); } /* Fade in and move to original position */
            85% { opacity: 1; transform: translateY(0); } /* Stay visible */
            100% { opacity: 0; transform: translateY(-8px); } /* Fade out and move upwards */
          }
        `}
      </style>
    </Box>
  );
};


function CourseListingPage() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedProgram, setSelectedProgram] = useState(null);

  const formRefs = {
    firstName: useRef(null), lastName: useRef(null), email: useRef(null), contact: useRef(null), institute: useRef(null), course: useRef(null), major: useRef(null), previousQualification: useRef(null), previousCollege: useRef(null), address: useRef(null), parentName: useRef(null), parentcontact: useRef(null), parentEmail: useRef(null), aadhar: useRef(null),
    brochureName: useRef(null), brochureEmail: useRef(null), brochureContact: useRef(null),
  };
  const commonInstitutes = ["Dr.Homi Bhabha State University"];
  const sliderImages = [side1, side2, side3, side4,side5];
  const rotatingLogosList = [logo1, logo2, logo3, logo4, logo5, logo6, logo7, logo2]; // Example: Duplicated logo2 for smoother loop
  const [currentSlide, setCurrentSlide] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "", middleName: "", lastName: "", email: "", contact: "", institute: "", course: "", major: "", previousQualification: "", previousCollege: "", address: "", parentName: "", parentcontact: "", parentEmail: "", course_major: "", aadhar: "", program_id: "", selectedProgram: "",
  });
  const [errors, setErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerified, setIsVerified] = useState({ email: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [formStep, setFormStep] = useState(1);

  const [showBrochureDialog, setShowBrochureDialog] = useState(false);
  const [brochureFormData, setBrochureFormData] = useState({ name: "", email: "", contact: "" });
  const [brochureFormErrors, setBrochureFormErrors] = useState({});
  const [brochureOtpInput, setBrochureOtpInput] = useState("");
  const [brochureEmailVerified, setBrochureEmailVerified] = useState(false);
  const [brochureOtpSending, setBrochureOtpSending] = useState(false);
  const [brochureOtpVerifying, setBrochureOtpVerifying] = useState(false);
  const [openBrochureOtpDialog, setOpenBrochureOtpDialog] = useState(false);
  const [currentBrochureData, setCurrentBrochureData] = useState({ url: "", programName: "", programId: null });


  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  // ***** MODIFIED useEffect for fetching courses *****
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/programs/");
        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) { /* ignore if response body is not json */ }
          throw new Error(errorData?.message || `Failed to fetch courses (status: ${response.status})`);
        }
        let data = await response.json();

        if (!Array.isArray(data)) {
            console.error("Fetched courses data is not an array:", data);
            throw new Error("Course data is not in the expected format.");
        }

        const BASE_PDF_URL = "https://hsbubackend.tdtlworld.com";
        const DUMMY_PDF_URL = `https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf`; // Used for unavailable brochures

        data = data.map(course => {
          let fullPdfUrl = DUMMY_PDF_URL; // Default to dummy if no valid PDF path
          if (course.pdf && typeof course.pdf === 'string' && course.pdf.trim() !== "") {
            const pdfPath = course.pdf.trim();
            if (pdfPath.startsWith('http://') || pdfPath.startsWith('https://')) {
              fullPdfUrl = pdfPath; // It's already a full URL
            } else if (pdfPath.startsWith('/')) {
              // Relative path like /program_pdfs/...
              fullPdfUrl = `${BASE_PDF_URL}${pdfPath}`;
            } else {
              // Relative path like program_pdfs/... (assuming it's relative to base URL root)
              fullPdfUrl = `${BASE_PDF_URL}/${pdfPath}`;
            }
          }
          return {
            ...course,
            brochure_url: fullPdfUrl
          };
        });
        setCourses(data);
      } catch (err) {
        console.error("Error fetching or processing courses:", err);
        setError(err.message);
        setFeedbackMessage(err.message || "Error fetching courses data.");
        setErrorSnackbar(true);
        setCourses([]); // Ensure courses is an empty array on error
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, []);
  // ***** END OF MODIFIED useEffect *****

  useEffect(() => {
    if (sliderImages.length === 0 || isLoading) return; // Don't run if no images or still loading
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
    }, 4000); // Change slide every 4 seconds
    return () => clearInterval(timer);
  }, [sliderImages.length, isLoading]);


  useEffect(() => {
    if (showForm && formRefs.firstName.current) {
      setTimeout(() => formRefs.firstName.current.focus(), 300); // Delay to ensure dialog is rendered
    }
  }, [showForm]);
  useEffect(() => {
    if (showBrochureDialog && formRefs.brochureName.current) {
      setTimeout(() => formRefs.brochureName.current.focus(), 100);
    }
  }, [showBrochureDialog]);

  const handleKeyDown = (e, nextField) => {
    if (e.key === "Enter" && nextField && formRefs[nextField]?.current) {
      e.preventDefault();
      formRefs[nextField].current.focus();
    }
  };

  const openProgramDetails = (program) => {
    setSelectedProgram(program);
  };

  const openForm = (program) => {
    if (program) {
      setFormData({
        // Reset all fields
        firstName: "", middleName: "", lastName: "", email: "", contact: "", institute: "", course: "", major: "", previousQualification: "", previousCollege: "", address: "", parentName: "", parentcontact: "", parentEmail: "", aadhar: "",
        // Pre-fill program specific data
        selectedProgram: program.program_name,
        course_major: program.program_name, // This seems to be pre-filled with program name, verify if this is intended vs student's actual major
        program_id: program.id.toString(), // Ensure program_id is a string if API expects it
      });
      setErrors({});
      setIsVerified({ email: false }); // Reset email verification status
      setShowForm(true);
      setFormStep(1); // Reset to first step
      setSelectedProgram(null); // Close details dialog if it was open
      setShowBrochureDialog(false); // Close brochure dialog if it was open
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setIsVerified({ email: false });
    setFormStep(1);
    setErrors({});
    // Optionally reset formData if you don't want it to persist
    // setFormData({ ...initialFormDataState }); 
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" }); // Clear error for this field
    // If email is changed, reset verification status
    if (name === "email") setIsVerified({ ...isVerified, email: false });
  };
  const handleAutocompleteChange = (name, value) => {
    setFormData({ ...formData, [name]: value || "" }); // Ensure value is not null/undefined
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };
  const validateFormStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      const fields = ["firstName", "lastName", "email", "contact", "aadhar"];
      fields.forEach((field) => { if (!formData[field]) newErrors[field] = "Required"; });
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
      if (formData.contact && !/^\d{10}$/.test(formData.contact)) newErrors.contact = "Invalid 10-digit contact number";
      if (formData.aadhar && !/^\d{12}$/.test(formData.aadhar)) newErrors.aadhar = "Invalid 12-digit Aadhaar number";
    } else if (step === 2) {
      const fields = ["institute", "course", "major", "previousQualification", "previousCollege", "address"];
      fields.forEach((field) => { if (!formData[field]) newErrors[field] = "Required"; });
    } else if (step === 3) {
      const fields = ["parentName", "parentcontact", "parentEmail"];
      fields.forEach((field) => { if (!formData[field]) newErrors[field] = "Required"; });
      if (formData.parentEmail && !/\S+@\S+\.\S+/.test(formData.parentEmail)) newErrors.parentEmail = "Invalid email format";
      if (formData.parentcontact && !/^\d{10}$/.test(formData.parentcontact)) newErrors.parentcontact = "Invalid 10-digit contact number";
    }
    setErrors(prevErrors => ({...prevErrors, ...newErrors})); // Merge new errors with existing ones for the current step.
    return Object.keys(newErrors).length === 0;
  };
  const validateFullForm = () => {
    let allErrors = {};
    // Validate all steps and collect errors
    const step1Valid = validateFormStep(1);
    allErrors = {...allErrors, ...errors}; // Collect errors after step 1 validation
    
    const step2Valid = validateFormStep(2);
    allErrors = {...allErrors, ...errors}; // Collect errors after step 2 validation

    const step3Valid = validateFormStep(3);
    allErrors = {...allErrors, ...errors}; // Collect errors after step 3 validation

    // A simpler overall check for fields that might not be covered by steps or missed
    const generalFields = ["firstName", "lastName", "email", "contact", "aadhar", "institute", "course", "major", "previousQualification", "previousCollege", "address", "parentName", "parentcontact", "parentEmail"];
    generalFields.forEach((field) => { if (!formData[field]) allErrors[field] = "Required"; });
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) allErrors.email = "Invalid email format";
    if (formData.contact && !/^\d{10}$/.test(formData.contact)) allErrors.contact = "Invalid 10-digit contact number";
    if (formData.aadhar && !/^\d{12}$/.test(formData.aadhar)) allErrors.aadhar = "Invalid 12-digit Aadhaar number";
    if (formData.parentEmail && !/\S+@\S+\.\S+/.test(formData.parentEmail)) allErrors.parentEmail = "Invalid email format";
    if (formData.parentcontact && !/^\d{10}$/.test(formData.parentcontact)) allErrors.parentcontact = "Invalid 10-digit contact number";

    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };
  const nextStep = () => {
    if (validateFormStep(formStep)) {
      setFormStep(formStep + 1);
      setTimeout(() => { // Focus after state update and re-render
        if (formStep === 1 && formRefs.institute.current) formRefs.institute.current.querySelector('input')?.focus(); // For Autocomplete
        else if (formStep === 2 && formRefs.parentName.current) formRefs.parentName.current.focus();
      }, 100);
    }
  };
  const prevStep = () => setFormStep(formStep - 1);
  const sendMainFormEmailOTP = async () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors({ ...errors, email: "Valid email required to send OTP" }); return;
    }
    setOtpSending(true);
    try {
      const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/send-otp/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await response.json();
      if (response.ok) {
        setOpenDialog(true);
        setFeedbackMessage(data.message || "OTP sent successfully!");
        setSuccessSnackbar(true);
      } else {
        setFeedbackMessage(data.message || "Failed to send OTP. Please try again."); setErrorSnackbar(true);
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      setFeedbackMessage("Network error while sending OTP. Please check your connection."); setErrorSnackbar(true);
    } finally { setOtpSending(false); }
  };
  const verifyMainFormOTP = async () => {
    if (!otp || otp.length < 6) { // Basic OTP validation
        setFeedbackMessage("Please enter a valid 6-digit OTP."); 
        setErrorSnackbar(true); // Or update an error message within the OTP dialog
        return;
    }
    setOtpVerifying(true);
    try {
      const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/verify-otp/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: otp }),
      });
      const data = await response.json();
      if (response.ok) {
        setIsVerified({ ...isVerified, email: true });
        setOpenDialog(false); setOtp(""); // Clear OTP input
        setFeedbackMessage("Email verified successfully!"); setSuccessSnackbar(true);
      } else {
        setFeedbackMessage(data.message || "Invalid OTP. Please try again."); setErrorSnackbar(true);
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setFeedbackMessage("Network error while verifying OTP."); setErrorSnackbar(true);
    } finally { setOtpVerifying(false); }
  };
  const handleSubmit = async () => {
    if (!validateFullForm()) {
        setFeedbackMessage("Please fill all required fields correctly.");
        setErrorSnackbar(true);
        return;
    }
    if (!isVerified.email) {
      setFeedbackMessage("Please verify your email before submitting."); setErrorSnackbar(true); return;
    }

    Swal.fire({ title: "Submitting Application...", text: "Please wait a moment.", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    setIsSubmitting(true);
    try {
      const payload = {
        first_name: formData.firstName, middle_name: formData.middleName || null, // Send null if empty
        last_name: formData.lastName,
        email: formData.email, phone_number: formData.contact, institute: formData.institute,
        college_course: formData.course, course_major: formData.major, // Ensure 'course_major' field from form is used if different from 'major'
        previous_qualification: formData.previousQualification, previous_college: formData.previousCollege,
        permanent_address: formData.address, parent_name: formData.parentName,
        aadhar: formData.aadhar, parent_phone: formData.parentcontact, parent_email: formData.parentEmail,
        program_id: formData.program_id, // This should be the ID of the selected program
      };
      const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/register/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json(); 
      
      if (response.ok) {
        Swal.fire("Application Submitted!", "Your application has been received successfully.", "success");
        handleFormClose(); 
      } else {
        Swal.fire("Submission Error", data.detail || data.message || "An error occurred. Please try again.", "error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      Swal.fire("Network Error", "Could not submit the form. Please check your internet connection and try again.", "error");
    } finally {
      // Swal.close(); // No need to explicitly close if using Swal.fire which auto-closes or is replaced
      setIsSubmitting(false);
    }
  };
  const renderFormStep = () => {
    switch (formStep) {
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: "500", color: "#6a1b9a", mb: 2 }}>Personal Information</Typography></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.firstName} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>First Name *</FormLabel>
                <TextField inputRef={formRefs.firstName} name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="First name" size="small" error={!!errors.firstName} onKeyDown={(e) => handleKeyDown(e, "middleName")} autoComplete="given-name" />
                {errors.firstName && <FormHelperText>{errors.firstName}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Middle Name</FormLabel>
                <TextField name="middleName" value={formData.middleName} onChange={handleInputChange} placeholder="Middle name (optional)" size="small" onKeyDown={(e) => handleKeyDown(e, "lastName")} autoComplete="additional-name" />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.lastName} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Last Name *</FormLabel>
                <TextField inputRef={formRefs.lastName} name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Last name" size="small" error={!!errors.lastName} onKeyDown={(e) => handleKeyDown(e, "email")} autoComplete="family-name" />
                {errors.lastName && <FormHelperText>{errors.lastName}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.email} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Email *</FormLabel>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField inputRef={formRefs.email} name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" size="small" fullWidth error={!!errors.email} disabled={isVerified.email} onKeyDown={(e) => handleKeyDown(e, "contact")} autoComplete="email" />
                  <Button variant="contained" onClick={sendMainFormEmailOTP} disabled={isVerified.email || otpSending || !formData.email || !!errors.email} sx={{ backgroundColor: isVerified.email ? "#4caf50" : "#6a1b9a", '&:hover': { backgroundColor: isVerified.email ? "#388e3c" : "#5e1687" }, minWidth: "100px", color: 'white', height: '40px', textTransform: 'none' }}>
                    {otpSending ? <CircularProgress size={24} color="inherit" /> : isVerified.email ? <><CheckCircle fontSize="small" sx={{ mr: 0.5 }} /> Verified</> : "Verify"}
                  </Button>
                </Box>
                {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.contact} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Contact Number *</FormLabel>
                <TextField inputRef={formRefs.contact} name="contact" value={formData.contact} onChange={handleInputChange} placeholder="10-digit contact" size="small" error={!!errors.contact} type="tel" autoComplete="tel" inputProps={{ maxLength: 10, pattern: "[0-9]*" }} onKeyDown={(e) => handleKeyDown(e, "aadhar")} />
                {errors.contact && <FormHelperText>{errors.contact}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.aadhar} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Aadhaar Card Number *</FormLabel>
                <TextField inputRef={formRefs.aadhar} name="aadhar" value={formData.aadhar} onChange={handleInputChange} placeholder="12-digit Aadhaar" size="small" error={!!errors.aadhar} type="text" inputProps={{ maxLength: 12, pattern: "[0-9]*" }} autoComplete="off" />
                {errors.aadhar && <FormHelperText>{errors.aadhar}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#6a1b9a", mb: 2 }}>Education Information</Typography></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.institute} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Institute/College *</FormLabel>
                <Autocomplete freeSolo options={commonInstitutes} value={formData.institute}
                  onChange={(event, newValue) => handleAutocompleteChange("institute", newValue)}
                  onInputChange={(event, newInputValue) => {
                    handleAutocompleteChange("institute", newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} inputRef={formRefs.institute} name="institute" placeholder="Your current institute" size="small" error={!!errors.institute}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.defaultPrevented) { e.preventDefault(); handleKeyDown(e, "course"); } }} // Check !e.defaultPrevented for Autocomplete's own Enter handling
                    />
                  )}
                />
                {errors.institute && <FormHelperText>{errors.institute}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.course} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>College Course *</FormLabel>
                <TextField select inputRef={formRefs.course} name="course" value={formData.course} onChange={handleInputChange} size="small" error={!!errors.course} onKeyDown={(e) => handleKeyDown(e, "major")} displayEmpty>
                 <MenuItem value="" disabled><em>Select Course</em></MenuItem>
                  {["B.Tech", "B.Sc", "M.Tech", "M.Sc", "BCA", "MCA", "Other"].map(opt => <MenuItem key={opt} value={opt.toLowerCase().replace(/[.\s]/g, '')}>{opt}</MenuItem>)}
                </TextField>
                {errors.course && <FormHelperText>{errors.course}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.major} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Course Major/Specialization *</FormLabel>
                <TextField inputRef={formRefs.major} name="major" value={formData.major} onChange={handleInputChange} placeholder="Your specialization" size="small" error={!!errors.major} onKeyDown={(e) => handleKeyDown(e, "previousQualification")} />
                {errors.major && <FormHelperText>{errors.major}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.previousQualification} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Previous Qualification *</FormLabel>
                <TextField inputRef={formRefs.previousQualification} name="previousQualification" value={formData.previousQualification} onChange={handleInputChange} placeholder="e.g., 12th Science, B.Sc. IT" size="small" error={!!errors.previousQualification} onKeyDown={(e) => handleKeyDown(e, "previousCollege")} />
                {errors.previousQualification && <FormHelperText>{errors.previousQualification}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.previousCollege} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Previous College/Institution *</FormLabel>
                <Autocomplete freeSolo options={commonInstitutes} value={formData.previousCollege}
                  onChange={(event, newValue) => handleAutocompleteChange("previousCollege", newValue)}
                  onInputChange={(event, newInputValue) => {
                    handleAutocompleteChange("previousCollege", newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} inputRef={formRefs.previousCollege} name="previousCollege" placeholder="Name of previous school/college" size="small" error={!!errors.previousCollege}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.defaultPrevented) { e.preventDefault(); handleKeyDown(e, "address"); } }}
                    />
                  )}
                />
                {errors.previousCollege && <FormHelperText>{errors.previousCollege}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.address} sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: 0.5 }}>Permanent Address *</FormLabel>
                <TextField inputRef={formRefs.address} name="address" value={formData.address} onChange={handleInputChange} placeholder="Your residential address" size="small" error={!!errors.address} multiline rows={2} autoComplete="street-address" />
                {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#6a1b9a", mb: 2 }}>Parent/Guardian Information</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth error={!!errors.parentName} sx={{ mb: 2 }}>
                      <FormLabel sx={{ mb: 0.5 }}>Parent Name *</FormLabel>
                      <TextField inputRef={formRefs.parentName} name="parentName" value={formData.parentName} onChange={handleInputChange} placeholder="Parent/Guardian name" size="small" error={!!errors.parentName} onKeyDown={(e) => handleKeyDown(e, "parentcontact")} />
                      {errors.parentName && <FormHelperText>{errors.parentName}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth error={!!errors.parentcontact} sx={{ mb: 2 }}>
                      <FormLabel sx={{ mb: 0.5 }}>Parent Contact *</FormLabel>
                      <TextField inputRef={formRefs.parentcontact} name="parentcontact" value={formData.parentcontact} onChange={handleInputChange} placeholder="10-digit contact" size="small" error={!!errors.parentcontact} type="tel" inputProps={{ maxLength: 10, pattern: "[0-9]*" }} onKeyDown={(e) => handleKeyDown(e, "parentEmail")} />
                      {errors.parentcontact && <FormHelperText>{errors.parentcontact}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth error={!!errors.parentEmail} sx={{ mb: 2 }}>
                      <FormLabel sx={{ mb: 0.5 }}>Parent Email *</FormLabel>
                      <TextField inputRef={formRefs.parentEmail} name="parentEmail" type="email" value={formData.parentEmail} onChange={handleInputChange} placeholder="parent@email.com" size="small" error={!!errors.parentEmail} />
                      {errors.parentEmail && <FormHelperText>{errors.parentEmail}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#6a1b9a", mb: 1 }}>Selected Program</Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 2, p:1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>{formData.selectedProgram}</Typography>
              </Box>
            </Grid>
          </Grid>
        );
      default: return null;
    }
  };
  const renderFormNavigation = () => {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
        {formStep > 1 ? (
          <Button variant="outlined" onClick={prevStep} sx={{ color: "#6a1b9a", borderColor: "rgba(106, 27, 154, 0.5)", '&:hover': {borderColor: "#6a1b9a", backgroundColor: 'rgba(106, 27, 154, 0.04)'} }}>Back</Button>
        ) : <Box /> /* Placeholder to keep 'Continue' on the right */}
        <Box sx={{ ml: "auto" }}>
          {formStep < 3 ? (
            <Button variant="contained" onClick={nextStep} sx={{ background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white" }}>Continue</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={!isVerified.email || isSubmitting} sx={{ background: "linear-gradient(45deg, #ec407a, #f06292)", color: "white", px: 3, py: 1.2, mt: 1, fontWeight: "bold", '&:disabled': { background: '#ccc'} }}>
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit Application"}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const openBrochureDownloadForm = (programForBrochure) => {
    if (!programForBrochure || !programForBrochure.brochure_url || programForBrochure.brochure_url.includes('dummy.pdf')) {
      Swal.fire("Brochure Not Available", "The brochure for this program is not available at the moment. Please check back later.", "info");
      return;
    }
    setCurrentBrochureData({ 
        url: programForBrochure.brochure_url, 
        programName: programForBrochure.program_name,
        programId: programForBrochure.id // Store program ID if needed for logging
    });
    setBrochureFormData({ name: "", email: "", contact: "" }); // Reset form
    setBrochureFormErrors({});
    setBrochureEmailVerified(false); // Reset verification
    setBrochureOtpInput("");
    setShowBrochureDialog(true);
    setSelectedProgram(null); // Close details dialog if open
  };

  const handleBrochureFormClose = () => {
    setShowBrochureDialog(false);
    setBrochureEmailVerified(false);
    setBrochureFormErrors({});
    // Optionally reset brochureFormData
  };

  const handleBrochureInputChange = (e) => {
    const { name, value } = e.target;
    setBrochureFormData({ ...brochureFormData, [name]: value });
    if (brochureFormErrors[name]) setBrochureFormErrors({ ...brochureFormErrors, [name]: "" });
    if (name === "email") setBrochureEmailVerified(false); // Reset verification if email changes
  };
  const validateBrochureForm = () => {
    const newErrors = {};
    if (!brochureFormData.name.trim()) newErrors.name = "Name is required";
    if (!brochureFormData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(brochureFormData.email)) newErrors.email = "Invalid email format";
    if (!brochureFormData.contact.trim()) newErrors.contact = "Contact number is required";
    else if (!/^\d{10}$/.test(brochureFormData.contact)) newErrors.contact = "Invalid 10-digit contact number";
    setBrochureFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const sendBrochureEmailOTP = async () => {
    if (!brochureFormData.email || !/\S+@\S+\.\S+/.test(brochureFormData.email)) {
      setBrochureFormErrors({ ...brochureFormErrors, email: "Please enter a valid email to send OTP" });
      return;
    }
    setBrochureOtpSending(true);
    try {
      const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/send-otp/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: brochureFormData.email }),
      });
      const data = await response.json();
      if (response.ok) {
        setOpenBrochureOtpDialog(true);
        setFeedbackMessage(data.message || "OTP sent to your email for brochure download!");
        setSuccessSnackbar(true);
      } else {
        setFeedbackMessage(data.message || "Failed to send OTP for brochure. Please try again.");
        setErrorSnackbar(true);
      }
    } catch (error) {
      console.error("Brochure OTP send error:", error);
      setFeedbackMessage("Network error sending OTP. Please check your connection."); setErrorSnackbar(true);
    } finally { setBrochureOtpSending(false); }
  };
  const verifyBrochureOTP = async () => {
    if (!brochureOtpInput || brochureOtpInput.length < 6) { // Basic OTP validation
        setFeedbackMessage("Please enter a valid 6-digit OTP.");
        setErrorSnackbar(true); // Or update error in OTP dialog
        return;
    }
    setBrochureOtpVerifying(true);
    try {
      const response = await fetch("https://hsbubackend.tdtlworld.com/formdata/verify-otp/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: brochureFormData.email, otp: brochureOtpInput }),
      });
      const data = await response.json();
      if (response.ok) {
        setBrochureEmailVerified(true);
        setOpenBrochureOtpDialog(false); setBrochureOtpInput(""); // Clear OTP
        setFeedbackMessage("Email verified! You can now view the brochure.");
        setSuccessSnackbar(true);
      } else {
        setFeedbackMessage(data.message || "Invalid OTP for brochure. Please try again.");
        setErrorSnackbar(true);
      }
    } catch (error) {
      console.error("Brochure OTP verify error:", error);
      setFeedbackMessage("Network error verifying OTP. Please check your connection."); setErrorSnackbar(true);
    } finally { setBrochureOtpVerifying(false); }
  };
  
  const handleDownloadBrochure = async () => {
    if (!validateBrochureForm()) {
      setFeedbackMessage("Please fill all required fields correctly and verify email.");
      setErrorSnackbar(true);
      return;
    }
    if (!brochureEmailVerified) {
       setFeedbackMessage("Please verify your email first to view the brochure."); // Updated message
       setErrorSnackbar(true);
      return;
    }
    if (!currentBrochureData.url || currentBrochureData.url.includes('dummy.pdf')) {
        Swal.fire("Brochure Not Found", "The brochure URL is missing or invalid.", "error");
        return;
    }

    // Log download attempt
    try {
        const logPayload = {
            name: brochureFormData.name,
            email: brochureFormData.email,
            mobile: brochureFormData.contact, // Mapped from 'contact' field
            // program_name: currentBrochureData.programName, // Optional: if API supports/requires
            // program_id: currentBrochureData.programId, // Optional: if API supports/requires
        };

        const logResponse = await fetch('https://hsbubackend.tdtlworld.com/formdata/log-download/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(logPayload)
        });

        const logData = await logResponse.json();
        if (logResponse.ok) {
            console.log('Brochure download/view logged successfully:', logData);
        } else {
            console.warn('Failed to log brochure download/view:', logData.detail || logData.message || logData);
            // Non-critical error, proceed. 
        }
    } catch (error) {
        console.error('Error logging brochure download/view:', error);
        // Proceed even if logging fails
    }
    
    // Open PDF in a new tab
    window.open(currentBrochureData.url, "_blank");
    
    Swal.fire({
        title: "Opening Brochure...", // Updated title
        text: `The brochure for ${currentBrochureData.programName} should open in a new tab.`, // Updated text
        icon: "success",
        timer: 3000,
        showConfirmButton: false
    });
    handleBrochureFormClose(); // Close the dialog
  };


  // ***** MODIFIED handleScrollDown *****
  const handleScrollDown = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      const headerElement = document.getElementById('app-header-sticky'); // Use ID for reliability
      const headerHeight = headerElement ? headerElement.offsetHeight : 0;
      
      const elementPosition = coursesSection.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight - 20; // 20px buffer below header

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
        // Fallback if the target section isn't found
        window.scrollBy({ top: window.innerHeight * 0.7, behavior: 'smooth' });
    }
  };
  // ***** END OF MODIFIED handleScrollDown *****

  return (
    <div>
      <AppHeader courses={courses} /> {/* Pass courses to AppHeader */}

      <Container maxWidth="lg" style={{ paddingTop: "20px", paddingBottom: "40px" }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 200px)"> {/* Adjust height based on header */}
            <CircularProgress style={{ color: "#6a1b9a" }} />
            <Typography sx={{ml: 2, color: "#6a1b9a"}}>Loading programs...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="calc(100vh - 200px)" sx={{ color: "#d32f2f", textAlign: "center", p: 3, backgroundColor: 'rgba(211, 47, 47, 0.05)', borderRadius: 2 }}>
            <Typography variant="h6">Failed to Load Programs</Typography>
            <Typography >
              {error} <br /> Please try refreshing the page or check your internet connection.
            </Typography>
          </Box>
        ) : (
          <>
            {sliderImages.length > 0 && (
              <Box sx={{ width: '100%', height: { xs: '250px', sm: '350px', md: '450px' }, overflow: 'hidden', position: 'relative', mb: 2, borderRadius: '12px', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}>
                {sliderImages.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Slide ${index + 1}`}
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      position: 'absolute', top: 0, left: 0,
                      opacity: index === currentSlide ? 1 : 0,
                      transition: 'opacity 1.2s ease-in-out', // Smooth transition
                    }}
                  />
                ))}
                <Box sx={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
                  {sliderImages.map((_, index) => (
                    <Box key={`dot-${index}`} sx={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      backgroundColor: index === currentSlide ? '#6a1b9a' : 'rgba(255,255,255,0.7)',
                      transition: 'background-color 0.3s'
                    }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Animated Scroll Down Indicator - shown only if there are courses */}
            {!isLoading && !error && courses.length > 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  my: {xs: 2, sm: 3}, 
                }}
              >
                <IconButton
                  aria-label="scroll down to courses"
                  onClick={handleScrollDown}
                  sx={{
                    color: '#6a1b9a',
                    animation: 'bounce 2s infinite',
                    width: '50px',
                    height: '50px',
                    border: '2px solid rgba(106, 27, 154, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(106, 27, 154, 0.05)',
                      animationPlayState: 'paused', 
                    }
                  }}
                >
                  <KeyboardArrowDown fontSize="large" />
                </IconButton>
              </Box>
            )}
            <style>
              {`
                @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% {
                    transform: translateY(0);
                  }
                  40% {
                    transform: translateY(-12px);
                  }
                  60% {
                    transform: translateY(-6px);
                  }
                }
              `}
            </style>

            <Box style={{ textAlign: "center", marginBottom: "40px", marginTop: "20px" }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: "bold", background: "linear-gradient(45deg, #6a1b9a, #ec407a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 2 }}>
                Explore Our Programs
              </Typography>
              <Typography variant="body1" sx={{ color: "#555", maxWidth: "800px", margin: "0 auto" }}>
                Discover a range of cutting-edge programs designed for future-focused professionals. Choose from various durations and flexible schedules to fit your learning needs.
              </Typography>
            </Box>
            {rotatingLogosList.length > 0 && (
              <Box sx={{ width: '100%', overflow: 'hidden', py: { xs: 2, sm: 3 }, backgroundColor: 'rgba(106, 27, 154, 0.02)', mb: 4, borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', animation: 'marqueeAnimation 40s linear infinite' }}>
                  {[...rotatingLogosList, ...rotatingLogosList].map((logo, index) => ( // Duplicate for seamless loop
                    <img key={index} src={logo || "/placeholder.svg"} alt={`Rotating Logo ${index}`}
                      style={{ height: '60px', width: 'auto', margin: '0 30px', flexShrink: 0, objectFit: 'contain' }}
                    />
                  ))}
                </Box>
                <style>
                  {`
                        @keyframes marqueeAnimation {
                            0% { transform: translateX(0%); }
                            100% { transform: translateX(-50%); }
                        }
                        `}
                </style>
              </Box>
            )}
            {/* Added id="courses-section" here */}
            <Paper id="courses-section" elevation={0} style={{ marginBottom: "32px", background: "rgba(106, 27, 154, 0.05)", borderRadius: '8px' }}>
              <Typography variant="h5" component="div" sx={{ textAlign: 'center', py: 2, color: '#6a1b9a', fontWeight: 'bold' }}>
                All Programs
              </Typography>
            </Paper>

            {courses.length > 0 ? (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
                {courses.map((program, index) => (
                  <Card
                    key={program.id || index} // Use program.id if available, otherwise index
                    sx={{
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', // Ensures actions are at bottom
                      height: "100%", borderTop: "4px solid #6a1b9a", borderRadius: "12px",
                      transition: "all 0.3s ease", background: "linear-gradient(135deg, rgba(250, 245, 255, 0.6), rgba(255, 248, 250, 0.6))",
                      boxShadow: "0 6px 18px rgba(0, 0, 0, 0.07)", '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(106, 27, 154, 0.15)' }
                    }}
                  >
                    <CardContent sx={{ pb: 1, flexGrow: 1 }}> {/* flexGrow to push actions down */}
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a", mb: 1, minHeight: '56px' /* approx 2 lines */ }}>{program.program_name}</Typography>
                      <Typography variant="body2" sx={{ color: "#555", mb: 2, minHeight: '40px' /* approx 2 lines */ }}>
                        {program.duration || 'N/A'} program with {program.mode || 'N/A'} learning.
                      </Typography>
                      <Grid container spacing={1.5} sx={{ mb: 2, fontSize: '0.875rem' }}>
                        {[
                          // { icon: <AccessTime sx={{ color: "#6a1b9a", fontSize: "18px" }} />, text: program.timing || 'N/A' },
                          { icon: <People sx={{ color: "#6a1b9a", fontSize: "18px" }} />, text: `Intake: ${program.intake || 'N/A'}` },
                          // { icon: <CalendarMonth sx={{ color: "#6a1b9a", fontSize: "18px" }} />, text: program.days || 'N/A' },
                          { icon: <MenuBook sx={{ color: "#6a1b9a", fontSize: "18px" }} />, text: program.mode || 'N/A' },
                        ].map((item, idx) => (
                          <Grid item xs={6} key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.icon}
                            <Typography variant="body2" sx={{ ml: 1 }}>{item.text}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                    <CardActions sx={{ padding: "0 16px 16px", justifyContent: 'space-between' }}>
                      <Button
                        variant="outlined"
                        sx={{ flex: 1, borderColor: "rgba(106, 27, 154, 0.5)", color: "#6a1b9a", mr: 1, '&:hover': { borderColor: "#6a1b9a", backgroundColor: 'rgba(106, 27, 154, 0.04)' } }}
                        onClick={() => openProgramDetails(program)}
                      >
                        View Details
                      </Button>
                      <Button variant="contained"
                        sx={{ flex: 1, background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white", '&:hover': { background: "linear-gradient(45deg, #5e1687, #d8326b)" } }}
                        onClick={() => openForm(program)}
                      >
                        Apply Now
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 4, fontStyle: 'italic' }}>
                No programs are currently available. Please check back later or contact us for more information.
              </Typography>
            )}
          </>
        )}

        {/* Program Details Dialog */}
        <Dialog
          open={selectedProgram !== null && !showForm && !showBrochureDialog} // Ensure it doesn't overlap with other dialogs
          onClose={() => setSelectedProgram(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", borderBottom: '1px solid #eee', p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>
                {selectedProgram?.program_name}
              </Typography>
              <IconButton onClick={() => setSelectedProgram(null)} size="small"><Close /></IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              Program details and options
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            {selectedProgram && (
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "16px",
                    p: 2,
                    backgroundColor: "rgba(106, 27, 154, 0.03)",
                    borderRadius: "8px",
                    mb: "20px",
                  }}
                >
                  {[
                    { label: "Duration", value: selectedProgram.duration },
                    { label: "Intake", value: `${selectedProgram.intake} ` },
                    { label: "Mode", value: selectedProgram.mode },
                    // { label: "Schedule : Full Week", value: selectedProgram.days }, // Assuming 'days' refers to schedule
                    // { label: "Timings", value: selectedProgram.timing }
                  ].map(item => (
                    item.value ? (
                      <Box key={item.label}>
                        <Typography variant="body2" sx={{ color: "#666", fontWeight: "medium" }}>{item.label}</Typography>
                        <Typography sx={{ fontWeight: "medium" }}>{item.value}</Typography>
                      </Box>
                    ) : null
                  ))}
                </Box>
                <Box sx={{ p: 2, backgroundColor: "rgba(236, 64, 122, 0.03)", borderRadius: "8px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Info sx={{ mr: 1, color: "#ec407a", fontSize: "20px" }} />
                    <Typography sx={{ fontWeight: "medium", color: "#ec407a" }}>Program Highlights</Typography>
                  </Box>
                  <ul style={{ paddingLeft: "24px", margin: "8px 0", fontSize: '0.9rem', color: '#555', listStyleType: 'disc' }}>
                    <li>Industry-relevant curriculum</li>
                    <li>Hands-on projects and case studies</li>
                    <li>Expert instructors with industry experience</li>
                    <li>Career support and placement assistance</li>
                    <li>Flexible learning options</li>
                  </ul>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px 24px", justifyContent: 'space-between', borderTop: '1px solid #eee' }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              sx={{ borderColor: "rgba(106, 27, 154, 0.5)", color: "#6a1b9a", '&:hover': { borderColor: "#6a1b9a", backgroundColor: 'rgba(106, 27, 154, 0.04)' } }}
              onClick={() => {
                if (selectedProgram) {
                  openBrochureDownloadForm(selectedProgram);
                }
              }}
              disabled={!selectedProgram?.brochure_url || selectedProgram.brochure_url.includes('dummy.pdf')}
            >
              View Brochure {/* Changed from Download Brochure */}
            </Button>
            <Button
              variant="contained"
              sx={{ background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white", '&:hover': { background: "linear-gradient(45deg, #5e1687, #d8326b)" } }}
              onClick={() => {
                if (selectedProgram) {
                  openForm(selectedProgram);
                }
              }}
            >
              Apply for this Program
            </Button>
          </DialogActions>
        </Dialog>

        {/* Application Form Dialog */}
        <Dialog open={showForm} onClose={handleFormClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "90vh", borderRadius: "12px" } }}>
          <DialogTitle sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", borderBottom: '1px solid #eee', p:2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>Application Form</Typography>
                <Typography variant="body2" sx={{ color: "#666", mt:0.5 }}>Fill all required fields to apply.</Typography>
              </Box>
              <IconButton onClick={handleFormClose} size="small"><Close /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px", overflowY: 'auto' }}> {/* Ensure content is scrollable */}
            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                {["Personal", "Education", "Parent Info"].map((stepName, index) => (
                  <Typography key={index} variant="caption" sx={{ 
                      fontWeight: formStep === index + 1 ? "bold" : "normal", 
                      color: formStep === index + 1 ? "#6a1b9a" : formStep > index + 1 ? "#4caf50" : "#999",
                      textAlign: 'center',
                      flex: 1,
                  }}>
                    {formStep > index + 1 ? <CheckCircle fontSize="small" sx={{verticalAlign: 'middle', mr: 0.5, color: '#4caf50'}}/> : ""}{stepName}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: "flex", height: "8px", borderRadius: "4px", overflow: "hidden", backgroundColor: "#e0e0e0" }}>
                <Box sx={{ width: `${(formStep / 3) * 100}%`, backgroundColor: "#6a1b9a", transition: "width 0.4s ease-in-out", borderRadius: "4px" }} />
              </Box>
            </Box>
            {renderFormStep()}
          </DialogContent>
            {/* Form Navigation moved outside DialogContent for stickiness if DialogContent scrolls */}
            {showForm && <DialogActions sx={{px:3, py:2, borderTop: '1px solid #eee'}}> 
              {renderFormNavigation()}
            </DialogActions>}
        </Dialog>

        {/* Main Form OTP Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", p:2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>Enter OTP</Typography>
            <Typography variant="body2" sx={{ color: "#666", mt:0.5 }}>Sent to {formData.email}</Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <TextField autoFocus margin="dense" label="OTP" type="text" fullWidth value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} // Allow only 6 digits
              placeholder="6-digit code" 
              inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px 24px" }}>
            <Button onClick={() => {setOpenDialog(false); setOtp("");}} sx={{ color: "#6a1b9a" }}>Cancel</Button>
            <Button onClick={verifyMainFormOTP} variant="contained" disabled={otpVerifying || !otp || otp.length < 6} sx={{ background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white", minWidth: "100px" }}>
              {otpVerifying ? <CircularProgress size={24} color="inherit" /> : "Verify"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Brochure Download Dialog */}
        <Dialog open={showBrochureDialog} onClose={handleBrochureFormClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", borderBottom: '1px solid #eee', p:2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>View Brochure</Typography> {/* Changed title */}
                <Typography variant="body2" sx={{ color: "#666", mt:0.5 }}>For: {currentBrochureData.programName}</Typography>
              </Box>
              <IconButton onClick={handleBrochureFormClose} size="small"><Close /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!brochureFormErrors.name} sx={{ mb: 1 }}>
                  <FormLabel sx={{ mb: 0.5 }}>Full Name *</FormLabel>
                  <TextField inputRef={formRefs.brochureName} name="name" value={brochureFormData.name} onChange={handleBrochureInputChange} size="small" placeholder="Your full name" error={!!brochureFormErrors.name}/>
                  {brochureFormErrors.name && <FormHelperText>{brochureFormErrors.name}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!brochureFormErrors.email} sx={{ mb: 1 }}>
                  <FormLabel sx={{ mb: 0.5 }}>Email Address *</FormLabel>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField inputRef={formRefs.brochureEmail} name="email" type="email" value={brochureFormData.email} onChange={handleBrochureInputChange} size="small" fullWidth placeholder="your@email.com" disabled={brochureEmailVerified} error={!!brochureFormErrors.email}/>
                    <Button variant="contained" onClick={sendBrochureEmailOTP} disabled={brochureEmailVerified || brochureOtpSending || !brochureFormData.email || !!brochureFormErrors.email} sx={{ backgroundColor: brochureEmailVerified ? "#4caf50" : "#6a1b9a", '&:hover': { backgroundColor: brochureEmailVerified ? "#388e3c" : "#5e1687" }, minWidth: "100px", color: 'white', height: '40px', textTransform: 'none' }}>
                      {brochureOtpSending ? <CircularProgress size={24} color="inherit" /> : brochureEmailVerified ? <><CheckCircle fontSize="small" sx={{ mr: 0.5 }} /> Verified</> : "Verify"}
                    </Button>
                  </Box>
                  {brochureFormErrors.email && <FormHelperText>{brochureFormErrors.email}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!brochureFormErrors.contact} sx={{ mb: 1 }}>
                  <FormLabel sx={{ mb: 0.5 }}>Contact Number *</FormLabel>
                  <TextField inputRef={formRefs.brochureContact} name="contact" value={brochureFormData.contact} onChange={handleBrochureInputChange} size="small" placeholder="10-digit contact" type="tel" inputProps={{ maxLength: 10, pattern: "[0-9]*" }} error={!!brochureFormErrors.contact}/>
                  {brochureFormErrors.contact && <FormHelperText>{brochureFormErrors.contact}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px 24px", borderTop: '1px solid #eee' }}>
            <Button onClick={handleBrochureFormClose} sx={{ color: "#666" }}>Cancel</Button>
            <Button onClick={handleDownloadBrochure} variant="contained" startIcon={<Download />} disabled={!brochureEmailVerified || brochureOtpVerifying } sx={{ background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white", '&:disabled': { background: '#ccc'} }}>
              {brochureOtpVerifying ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
              View Brochure {/* Changed button text */}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Brochure OTP Dialog */}
        <Dialog open={openBrochureOtpDialog} onClose={() => setOpenBrochureOtpDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ backgroundColor: "rgba(106, 27, 154, 0.05)", p:2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>Enter Brochure OTP</Typography>
            <Typography variant="body2" sx={{ color: "#666", mt:0.5 }}>Sent to {brochureFormData.email}</Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <TextField autoFocus margin="dense" label="OTP" type="text" fullWidth value={brochureOtpInput}
              onChange={(e) => setBrochureOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code" inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
            />
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px 24px" }}>
            <Button onClick={() => {setOpenBrochureOtpDialog(false); setBrochureOtpInput("");}} sx={{ color: "#6a1b9a" }}>Cancel</Button>
            <Button onClick={verifyBrochureOTP} variant="contained" disabled={brochureOtpVerifying || !brochureOtpInput || brochureOtpInput.length < 6} sx={{ background: "linear-gradient(45deg, #6a1b9a, #ec407a)", color: "white", minWidth: "100px" }}>
              {brochureOtpVerifying ? <CircularProgress size={24} color="inherit" /> : "Verify"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars for feedback */}
        <Snackbar open={successSnackbar} autoHideDuration={4000} onClose={() => setSuccessSnackbar(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setSuccessSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>{feedbackMessage}</Alert>
        </Snackbar>
        <Snackbar open={errorSnackbar} autoHideDuration={5000} onClose={() => setErrorSnackbar(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setErrorSnackbar(false)} severity="error" variant="filled" sx={{ width: "100%" }}>{feedbackMessage}</Alert>
        </Snackbar>

      </Container>
      <Chatbot courses={courses} /> 
    </div>
  );
}

export default CourseListingPage;